import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    DO_NOTHING,
    FEATURE_DEMO_ORGANISATION,
    FEATURE_DYNAMIC_REPORTING,
    FEATURE_OUTSTANDING_TASKS_VIEW,
    hasAnyPermission,
    hasFeatureFlag,
    PersonsOrganisationDto,
    PreferencesService,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';

const DEMO_DASHBOARD_HELP_VIEWED_PREFERENCES = 'demoDashboardHelpViewed';

const NOT_LOADED = {};

/**
 * This component is currently designed to be reloaded on each route change.
 */
@Component({
    selector: 'section-selector',
    templateUrl: './section-selector.component.html',
    styleUrls: ['./section-selector.component.scss']
})
export class SectionSelectorComponent implements OnDestroy {
    // This controls the "Explore This Page" tag that needs to be managed separately to the colour fadeout
    hideExploreBtn = true;
    modalClose = false;
    hasDynamicReporting$: Observable<boolean>;
    hasOutstandingTasts$: Observable<boolean>;
    canAdminOrganisation$: Observable<boolean>;
    helpComponentIdentifier$: Observable<string | null>;
    isDemoOrganisation$: Observable<boolean>;
    isInPreference$: Observable<boolean>;

    private ngUnsubscribe$ = new Subject<void>();

    private demoDashboardHelpViewedSource = new BehaviorSubject<{
        [componentName: string]: string | undefined;
    }>(NOT_LOADED);

    constructor(
        private sharedService: WorkspaceStateService,
        activatedRoute: ActivatedRoute,
        private preferencesService: PreferencesService,
        private router: Router
    ) {
        this.preferencesService
            .findPreferences(DEMO_DASHBOARD_HELP_VIEWED_PREFERENCES, null)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(preferences =>
                this.demoDashboardHelpViewedSource.next(preferences ?? {})
            );

        this.hasDynamicReporting$ =
            this.sharedService.currentOrganisation$.pipe(
                map(
                    o =>
                        o.type === 'organisation_loaded' &&
                        hasFeatureFlag(
                            o.organisation,
                            FEATURE_DYNAMIC_REPORTING
                        )
                )
            );

        this.hasOutstandingTasts$ =
            this.sharedService.currentOrganisation$.pipe(
                map(
                    o =>
                        o.type === 'organisation_loaded' &&
                        hasFeatureFlag(
                            o.organisation,
                            FEATURE_OUTSTANDING_TASKS_VIEW
                        )
                )
            );

        this.canAdminOrganisation$ =
            this.sharedService.currentOrganisation$.pipe(
                map(
                    o =>
                        o.type === 'organisation_loaded' &&
                        !o.organisation.personalOrganisation &&
                        (hasAnyPermission(o.organisation, [
                            PersonsOrganisationDto.Permissions
                                .ORGANISATION_EDIT,
                            PersonsOrganisationDto.Permissions
                                .FLIGHT_OPERATIONS_CONFIGURATION_VIEW
                        ]) ||
                            o.organisation.owner)
                )
            );

        this.helpComponentIdentifier$ = activatedRoute.data.pipe(
            map(d => d.enhancedHelpComponentIdentifier ?? null)
        );

        this.isDemoOrganisation$ = this.sharedService.currentOrganisation$.pipe(
            map(
                o =>
                    o.type === 'organisation_loaded' &&
                    hasFeatureFlag(o.organisation, FEATURE_DEMO_ORGANISATION)
            )
        );

        this.isInPreference$ = combineLatest([
            this.helpComponentIdentifier$,
            this.demoDashboardHelpViewedSource
        ]).pipe(
            map(
                ([componentName, shouldHide]) =>
                    // Start in a state as if the user has clicked on it
                    shouldHide === NOT_LOADED ||
                    (componentName != null && shouldHide[componentName] != null)
            )
        );

        // Reset the hideExploreBtn whenever the current dash changes.
        combineLatest([
            this.helpComponentIdentifier$,
            this.demoDashboardHelpViewedSource.pipe(
                filter(p => p !== NOT_LOADED)
            )
        ])
            .pipe(
                distinctUntilChanged(
                    (previous, current) => previous[0] === current[0]
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(
                ([componentName, existingPreferences]) =>
                    (this.hideExploreBtn =
                        existingPreferences[componentName] != null)
            );
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.demoDashboardHelpViewedSource.complete();
    }

    updatePreferences() {
        combineLatest([
            this.helpComponentIdentifier$,
            this.demoDashboardHelpViewedSource
        ])
            .pipe(
                take(1),
                filter(
                    ([componentName, existingPreferences]) =>
                        existingPreferences[componentName] == null
                ),
                switchMap(([componentName, existingPreferences]) => {
                    const currentPreferences = existingPreferences || {};
                    const updatedPreferences = {
                        ...currentPreferences,
                        [componentName]: toTimestamp(new Date())
                    };
                    return this.preferencesService
                        .updatePreferences(
                            DEMO_DASHBOARD_HELP_VIEWED_PREFERENCES,
                            null,
                            updatedPreferences
                        )
                        .pipe(
                            tap(() =>
                                this.demoDashboardHelpViewedSource.next(
                                    updatedPreferences
                                )
                            )
                        );
                }),

                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(DO_NOTHING);
    }

    onModalClosed() {
        this.modalClose = true;
        setTimeout(() => {
            this.hideExploreBtn = true;
        }, 500);
    }

    goToOnboarding() {
        this.router.navigate(['onboarding']);
    }
}
